import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'

import { Subject } from 'rxjs'

import { KeyNodeType } from '@overtur-monorepo/keying-shared'
import { take } from 'rxjs/operators'
import { MasterKey } from '@overtur-monorepo/keying-hierarchy'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { DialogService } from '../../services/dialog.service'
import { KeyingDataSetsService } from '../../services/keying.data.sets.service'
import { LoadingService } from '../../services/loading.service'
import { LoggingService } from '../../services/logging.service'
import { ChangeLogConfirmationDialogComponent } from '../../components/matrix/change-log-confirmation-dialog/change-log-confirmation-dialog.component'

@Component({
    selector: 'app-rename-keys-dialog',
    templateUrl: './rename-keys-dialog.component.html',
    styleUrls: ['./rename-keys-dialog.component.scss'],
    standalone: false
})
export class RenameKeysDialogComponent implements OnInit, OnDestroy {
  destroy$ = new Subject()
  keyNodeType = KeyNodeType
  renameForm: FormGroup
  renameSingleForm: FormGroup
  selectiveData: any
  selectedKey
  formData: any

  max = 1

  initialTab: number

  userInputtedExistingSymbols = []
  userInputtedNewSymbols = []

  masterKeyList = []

  publishedKeySymbolList: string[] = []

  focus = {
    prefix: false,
    from: false,
    to: false,
    suffix: false,
  }

  public errorMessages = {
    existingPrefix: [
      { type: 'required', message: 'Prefix is required.' },
      { type: 'beginsWithX', message: 'Can not start with X.' },
    ],
    existingFrom: [{ type: 'required', message: 'From is required.' }],
    existingTo: [{ type: 'required', message: 'To is required.' }],
    existingSuffix: [{ type: 'required', message: 'Suffix is required.' }],
    newPrefix: [
      { type: 'required', message: 'Prefix is required.' },
      { type: 'beginsWithX', message: 'Can not start with X.' },
    ],
    newFrom: [{ type: 'required', message: 'From is required.' }],
    newTo: [{ type: 'required', message: 'To is required.' }],
    newSuffix: [{ type: 'required', message: 'Suffix is required.' }],
  }

  prefix = ''

  showMultipleKeysOption = true
  constructor(
    public form: FormBuilder,
    private dialogRef: MatDialogRef<RenameKeysDialogComponent>,
    private dialogService: DialogService,
    private keyingDataSetsService: KeyingDataSetsService,
    private loadingService: LoadingService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private loggingService: LoggingService
  ) {
    this.selectiveData = data
    this.selectedKey = this.selectiveData?.masterKey.keySetSymbol
    this.createForm()
  }

  /* istanbul ignore next */
  ngOnInit() {
    if (
      this.selectiveData?.masterKey?.masterKeyTypeId === KeyNodeType.ChangeKeyThruNode ||
      this.selectiveData?.masterKey?.masterKeyTypeId === KeyNodeType.SingleKeyDifferentThruNode
    ) {
      this.initialTab = 1
      this.populateThruNotation(this.selectiveData?.masterKey)
    } else {
      if (this.selectiveData?.masterKey?.masterKeyTypeId === KeyNodeType.CrossKeyChild) {
        this.showMultipleKeysOption = !this.showMultipleKeysOption
      }
      this.initialTab = 0
      this.renameSingleForm.controls.existingKeyName.setValue(this.selectiveData?.masterKey?.keySetSymbol)
    }
    this.getMasterKeysInSystem()
  }

  /* istanbul ignore next */
  ngOnDestroy(): void {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  /* istanbul ignore next */
  createForm(): void {
    this.renameForm = this.form.group({
      existingPrefix: new FormControl('', [this.beginsWithXValidator]),
      existingFrom: new FormControl('', [Validators.required]),
      existingTo: new FormControl('', [Validators.required]),
      existingSuffix: new FormControl(''),
      newPrefix: new FormControl('', [this.beginsWithXValidator]),
      newFrom: new FormControl('', [Validators.required]),
      newTo: new FormControl('', [Validators.required]),
      newSuffix: new FormControl(''),
    })

    this.renameSingleForm = this.form.group({
      existingKeyName: new FormControl('', [Validators.required]),
      newKeyName: new FormControl('', [Validators.required]),
    })
  }

  beginsWithXValidator = (control: FormControl) => {
    let value = control.value
    if (value !== '') {
      const beginsWithX = value.charAt(0).toUpperCase() === 'X'
      if (beginsWithX) {
        return { beginsWithX, type: 'beginsWithX' }
      }
    }
    return null
  }

  /* istanbul ignore next */
  populateThruNotation(masterKey: MasterKey) {
    // Populates the Rename Keys Form onInit
    if (masterKey.keySetSymbol.includes('-')) {
      this.renameForm.controls.existingPrefix.setValue(masterKey.prefix)
      this.renameForm.controls.existingFrom.setValue(masterKey.from)
      this.renameForm.controls.existingTo.setValue(masterKey.to)
      this.renameForm.controls.existingSuffix.setValue(masterKey.suffix)
    } else {
      this.initialTab = 0
      this.renameSingleForm.controls.existingKeyName.setValue(masterKey.keySetSymbol)
    }
  }

  setThruNotation() {
    // Checks User Inputs for updated values
    if (this.renameForm.controls.existingFrom.value !== '') {
      const existingThruPrefix = this.renameForm.controls.existingPrefix.value.toString().toUpperCase()
      const existingThruFrom = this.renameForm.controls.existingFrom.value.toString().toUpperCase()
      const existingThruTo = this.renameForm.controls.existingTo.value.toString().toUpperCase()
      const existingThruSuffix = this.renameForm.controls.existingSuffix.value.toString().toUpperCase()
      for (let i = existingThruFrom; i <= existingThruTo; i++) {
        this.userInputtedExistingSymbols.push(existingThruPrefix + i + existingThruSuffix)
      }
    }

    if (this.renameForm.controls.newFrom.value !== '') {
      const newThruPrefix = this.renameForm.controls.newPrefix.value.toString().toUpperCase()
      const newThruFrom = this.renameForm.controls.newFrom.value.toString().toUpperCase()
      const newThruTo = this.renameForm.controls.newTo.value.toString().toUpperCase()
      const newThruSuffix = this.renameForm.controls.newSuffix.value.toString().toUpperCase()
      for (let i = newThruFrom; i <= newThruTo; i++) {
        this.userInputtedNewSymbols.push(newThruPrefix + i + newThruSuffix)
      }
    }
  }

  /* istanbul ignore next */
  submit() {
    if (this.renameForm.valid) {
      this.formData = {
        existingSymbolsRequestDto: {
          prefix: this.renameForm.controls.existingPrefix.value,
          from: this.renameForm.controls.existingFrom.value,
          to: this.renameForm.controls.existingTo.value,
          suffix: this.renameForm.controls.existingSuffix.value,
        },
        updatedSymbolsRequestDto: {
          prefix: this.renameForm.controls.newPrefix.value,
          from: this.renameForm.controls.newFrom.value,
          to: this.renameForm.controls.newTo.value,
          suffix: this.renameForm.controls.newSuffix.value,
        },
      }
      if (this.publishedKeySymbolList.length > 0) {
        const confirmData = {
          message: `The following key(s) are published: ${this.publishedKeySymbolList.join(
            ', '
          )}. Are you sure you want to rename the key(s)?.`,
          showCancel: true,
          cancel: 'Cancel',
          confirm: 'Confirm',
          reasonRequired: true,
          actionType: 'Rename Key',
        }
        const dialog = this.dialogService.open(ChangeLogConfirmationDialogComponent, confirmData, false)
        // const dialog = this.dialogService.open(ConfirmDialogComponent, confirmData, false)
        dialog
          .afterClosed()
          .pipe(take(1))
          .subscribe((result) => {
            if (result.confirmed) {
              this.formData = {
                existingSymbolsRequestDto: {
                  prefix: this.renameForm.controls.existingPrefix.value,
                  from: this.renameForm.controls.existingFrom.value,
                  to: this.renameForm.controls.existingTo.value,
                  suffix: this.renameForm.controls.existingSuffix.value,
                },
                updatedSymbolsRequestDto: {
                  prefix: this.renameForm.controls.newPrefix.value,
                  from: this.renameForm.controls.newFrom.value,
                  to: this.renameForm.controls.newTo.value,
                  suffix: this.renameForm.controls.newSuffix.value,
                },
                auditTrailRequestDto: {
                  reasonCode: result.auditTrailRequest.reasonCode,
                  description: result.auditTrailRequest.description,
                },
              }
              this.dialogRef.close(this.formData)
              // this.errorService.confirm('Book has been moved.')
            }
          })
      } else {
        const confirmData = {
          message: `Are you sure you want to rename the key(s)?.`,
          showCancel: true,
          cancel: 'Cancel',
          confirm: 'Confirm',
          reasonRequired: false,
        }
        const dialog = this.dialogService.open(ConfirmDialogComponent, confirmData, false)
        dialog
          .afterClosed()
          .pipe(take(1))
          .subscribe((result) => {
            if (result.confirmed) {
              this.dialogRef.close(this.formData)
              // this.errorService.confirm('Book has been moved.')
            }
          })
      }
    }
  }

  /* istanbul ignore next */
  submitSingleKey() {
    if (this.renameSingleForm.valid) {
      this.formData = {
        existingSymbolsRequestDto: {
          keySetSymbol: this.renameSingleForm.controls.existingKeyName.value.toUpperCase(),
        },
        updatedSymbolsRequestDto: {
          keySetSymbol: this.renameSingleForm.controls.newKeyName.value.toUpperCase(),
        },
      }
      if (this.publishedKeySymbolList.length > 0) {
        const confirmData = {
          message: `The following key(s) are published: ${this.publishedKeySymbolList.join(
            ', '
          )}. Are you sure you want to rename the key(s)?.`,
          showCancel: true,
          cancel: 'Cancel',
          confirm: 'Confirm',
          reasonRequired: true,
          actionType: 'Rename Key',
        }
        const dialog = this.dialogService.open(ChangeLogConfirmationDialogComponent, confirmData, false)
        dialog
          .afterClosed()
          .pipe(take(1))
          .subscribe((result) => {
            if (result.confirmed) {
              this.formData = {
                existingSymbolsRequestDto: {
                  keySetSymbol: this.renameSingleForm.controls.existingKeyName.value.toUpperCase(),
                },
                updatedSymbolsRequestDto: {
                  keySetSymbol: this.renameSingleForm.controls.newKeyName.value.toUpperCase(),
                },
                auditTrailRequestDto: {
                  reasonCode: result.auditTrailRequest.reasonCode,
                  description: result.auditTrailRequest.description,
                },
              }
              this.dialogRef.close(this.formData)
            }
          })
      } else {
        const confirmData = {
          message: `Are you sure you want to rename the key(s)?.`,
          showCancel: true,
          cancel: 'Cancel',
          confirm: 'Confirm',
          reasonRequired: false,
        }
        const dialog = this.dialogService.open(ConfirmDialogComponent, confirmData, false)
        dialog
          .afterClosed()
          .pipe(take(1))
          .subscribe((result) => {
            if (result.confirmed) {
              this.dialogRef.close(this.formData)
              // this.errorService.confirm('Book has been moved.')
            }
          })
      }
    }
  }

  //get all eligible keys that can be renamed within system
  /* istanbul ignore next */
  getMasterKeysInSystem() {
    this.keyingDataSetsService.keySystemDataSets$.pipe(take(1)).subscribe((key) => {
      const keys = key.masterKeysKeying
      this.loggingService.log('Keys:', keys)
      this.masterKeyList = keys.squashIncludeChangeKeys()
      this.loggingService.log('Masterkey List:', this.masterKeyList)
    })
  }

  /* istanbul ignore next */
  checkAssignedKeyCombinations() {
    this.userInputtedExistingSymbols = []
    this.userInputtedNewSymbols = []
    this.setThruNotation()
    this.loadingService.set({ isLoading: true, message: 'Checking for Key Assignments' })
    this.organizeAllCombos(true)
  }

  /* istanbul ignore next */
  checkSingleKeyCombination() {
    this.userInputtedExistingSymbols = [this.renameSingleForm.controls.existingKeyName.value.toUpperCase()]
    this.userInputtedNewSymbols = [this.renameSingleForm.controls.newKeyName.value.toUpperCase()]
    this.organizeAllCombos(false)
  }

  /* istanbul ignore next */
  organizeAllCombos(multipleKeys: boolean) {
    let checkMasterKeyList = this.masterKeyList.filter((key) =>
      this.userInputtedExistingSymbols.includes(key.keySetSymbol)
    )

    let assignedKeys = checkMasterKeyList.filter((p) => p.isPublished)
    this.publishedKeySymbolList = assignedKeys.map((p) => p.keySetSymbol)
    // uniqueIds = [...new Set(uniqueIds)]

    this.loadingService.set({ isLoading: false, message: '' })
    if (multipleKeys) {
      this.submit()
    } else {
      this.submitSingleKey()
    }
  }

  /* istanbul ignore next */
  focusOn(focus) {
    this.focus[focus] = !this.focus[focus]
  }

  /* istanbul ignore next */
  fieldHasErrorMessages(form, validation, fieldName) {
    const field = form.get(fieldName)

    return Boolean(field.hasError(validation.type) && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  fieldHasError(form, fieldName) {
    const field = form.get(fieldName)

    return Boolean(field.invalid && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  close() {
    this.dialogRef.close()
  }

  /* istanbul ignore next */
  disableAllFormFields() {
    this.renameForm.controls.prefix.disable()
    this.renameForm.controls.from.disable()
    this.renameForm.controls.to.disable()
    this.renameForm.controls.suffix.disable()
  }
}
